import type { CommonTranslationFunction } from '@autone/translations';

import type {
  CreateSidebarItemParams,
  SidebarItem,
} from '../hooks/use-app-sidebar/useAppSidebar.types';
import type {
  NavigationItem,
  SidebarSubNavigationItemProps,
} from '../types/Sidebar.types';

import { createSidebarSubItem } from './createSidebarSubItem';
import { getIsActive } from './getIsActive';

interface CreateSidebarItemProps {
  config: CreateSidebarItemParams;
  common: CommonTranslationFunction;
  isAuthorized: (permissions: string[]) => boolean;
  currentPathname: string;
  withClientRouting: (path: string) => NavigationItem;
}

/**
 * Creates a sidebar item from configuration parameters
 * @param config - Configuration parameters for the sidebar item
 * @param common - Translation function to get localized strings
 * @param isAuthorized - Authorization check function
 * @param currentPathname - Current route pathname for active state detection
 * @param withClientRouting - Function to handle client-side routing
 * @returns Configured sidebar item
 * @example
 * ```typescript
 * const item = createSidebarItem({
 *   config: {
 *     titleKey: 'nav.dashboard',
 *     path: '/dashboard',
 *     icon: DashboardIcon,
 *     permissions: ['DASHBOARD_READ'],
 *   },
 *   common: (key) => translate(key),
 *   isAuthorized: (perms) => checkAuth(perms),
 *   currentPathname: '/current/path',
 *   withClientRouting: (path) => ({ url: path })
 * });
 * **/
export const createSidebarItem = ({
  config,
  common,
  isAuthorized,
  currentPathname,
  withClientRouting,
}: CreateSidebarItemProps): SidebarItem => {
  // Create sub-items if they exist
  const subItems: SidebarSubNavigationItemProps[] | undefined =
    config.subItems?.map((subItem) =>
      createSidebarSubItem({
        subItemConfig: subItem,
        common,
        currentPathname,
        withClientRouting,
      }),
    );

  return {
    title: common(config.titleKey),
    path: config.path,
    icon: config.icon,
    isActive: getIsActive({
      href: config.path,
      currentPathname,
      path: config.path,
    }),
    isDisabled: !isAuthorized(config.permissions),
    ...withClientRouting(config.path),
    ...(subItems && { subItems }),
  };
};
